import React, { useState, useEffect } from "react";
import { Typography } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import { LoadingSpinner } from "ctech-react-components";
import { DealerProps } from "../../../../store/modules/DealerCare/types.store";
import { updateMerchantCategory } from "../../../../services/paymentApi";
import AddAcquirerModal from "./AddAcquirerModal";
import { mccCodes, type MccCode } from "./mccCodes";
import * as Styled from "./styles";
import type { ProcessedProvider } from "../../helpers";
import type { Provider } from "../DealerAcquirersTable/types";
import { convertProcessedToAvailable } from "../../utils/providerConverter";
import { getErrorMessage } from "../../utils/errorHandler";

export interface DealerPaymentConfigProps {
  dealerData: DealerProps | null;
  onMccUpdate?: (categoryCode: string) => void;
  onProviderAdded?: (
    provider?: Provider,
    meta?: { additionalStepPresented?: boolean },
  ) => void;
  merchantInfo?: { categoryCode: string } | null;
  availableProviders?: ProcessedProvider[];
  dealerProviders?: Provider[];
  hasProviders?: boolean;
}

interface MccFormData {
  mccCategory: MccCode | null;
}

const DealerPaymentConfig: React.FC<DealerPaymentConfigProps> = ({
  dealerData,
  onMccUpdate,
  onProviderAdded,
  merchantInfo,
  availableProviders,
  dealerProviders,
  hasProviders,
}) => {
  const { t } = useTranslation();
  const [isAddAcquirerModalOpen, setIsAddAcquirerModalOpen] = useState(false);
  const [isEditingMcc, setIsEditingMcc] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { control, handleSubmit, reset } = useForm<MccFormData>({
    defaultValues: {
      mccCategory: null,
    },
  });

  useEffect(() => {
    const mccValue = dealerData?.mcc;
    const mccCode =
      typeof mccValue === "object" && mccValue !== null
        ? (mccValue as { code?: string }).code
        : mccValue;

    const initialMcc = mccCodes.find(
      (mcc) => Number(mcc.code) === Number(mccCode),
    );
    reset({ mccCategory: initialMcc || null });
  }, [dealerData?.mcc, reset]);

  const handleEditMcc = () => {
    setIsEditingMcc(true);
  };

  const onSubmit = async (data: MccFormData) => {
    if (!data.mccCategory?.code || !dealerData?.id) {
      return;
    }

    try {
      setIsLoading(true);

      await updateMerchantCategory({
        merchantId: dealerData.id,
        categoryCode: data.mccCategory.code,
      });

      toast("Categoria MCC atualizada com sucesso!", {
        type: "success",
      });

      if (onMccUpdate) {
        onMccUpdate(data.mccCategory.code);
      }

      setIsEditingMcc(false);
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível atualizar a categoria MCC. Tente novamente!",
      );
      toast(errorMessage, { type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddAcquirer = () => {
    setIsAddAcquirerModalOpen(true);
  };

  const handleCloseAddAcquirerModal = () => {
    setIsAddAcquirerModalOpen(false);
  };

  return (
    <Styled.Container>
      <LoadingSpinner isSpinnerVisible={isLoading} />
      <Styled.Header>
        <Styled.HeaderLeft>
          <Typography variant="caption">{t("onlinePayment")}</Typography>
          {hasProviders ? (
            <Styled.ChipActive label={t("onlinePaymentActive")} size="small" />
          ) : (
            <Styled.ChipInactive
              label={t("onlinePaymentInactive")}
              size="small"
            />
          )}
        </Styled.HeaderLeft>
      </Styled.Header>

      <Styled.Content>
        <Styled.FormRow>
          <Controller
            name="mccCategory"
            control={control}
            render={({ field }) => (
              <Styled.AutocompleteStyled
                options={mccCodes}
                getOptionLabel={(option: MccCode) =>
                  `${option.code} - ${option.description}`
                }
                isOptionEqualToValue={(option: MccCode, value: MccCode) =>
                  option.code === value.code
                }
                value={field.value}
                onChange={(_, newValue) => field.onChange(newValue)}
                disabled={!isEditingMcc || isLoading}
                renderInput={(params) => (
                  <Styled.TextFieldStyled
                    {...params}
                    label={t("categoryMcc")}
                    placeholder="Pesquisar MCC..."
                    isEditing={isEditingMcc}
                  />
                )}
              />
            )}
          />

          {!isEditingMcc ? (
            <Styled.EditMccButton
              onClick={handleEditMcc}
              variant="outlined"
              disabled={isLoading}
            >
              {t("editMcc")}
            </Styled.EditMccButton>
          ) : (
            <Styled.SaveMccButton
              onClick={handleSubmit(onSubmit)}
              variant="outlined"
              disabled={isLoading}
            >
              Salvar MCC
            </Styled.SaveMccButton>
          )}
        </Styled.FormRow>

        <Styled.AddAcquirerButton
          onClick={handleAddAcquirer}
          color="inherit"
          variant="text"
          startIcon={<AddIcon />}
          disabled={isLoading}
        >
          {t("addAcquirer")}
        </Styled.AddAcquirerButton>
      </Styled.Content>

      {isAddAcquirerModalOpen && (
        <AddAcquirerModal
          open={isAddAcquirerModalOpen}
          onClose={handleCloseAddAcquirerModal}
          onSuccess={onProviderAdded}
          merchantInfo={merchantInfo}
          availableProviders={
            availableProviders
              ? convertProcessedToAvailable(availableProviders)
              : undefined
          }
          dealerProviders={dealerProviders}
        />
      )}
    </Styled.Container>
  );
};

export default DealerPaymentConfig;
